// ©2024 Austin App House. All rights reserved.

import 'react-datepicker/dist/react-datepicker.css';
import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import axios from 'axios';
import { connect } from 'react-redux';
import { ResourceDataTable } from '../../components/datatables/ResourceDataTable';
import {
  datatableAddedFundsColumns,
  datatablePayoutFundsColumns,
  localCurrencySettings,
} from '../../utils/constans';
import { toggleModal } from '../../storage/actions/modalsActions';
import { emitApplyFilters } from '../../components/datatables/filtersBus';
import DashboardIcon from '../../components/dashboard/icons/DashboardIcon';

const toYmd = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const TABS = [
  { id: 'added', label: 'Added Funds' },
  { id: 'payouts', label: 'Contractor Payouts' },
];

// A payout to a contractor always carries a work_order_id, while funds added
// by the company do not. `{ field: null }` also matches missing fields in Mongo,
// so each tab reliably queries only its own transactions.
const TAB_BASE_FILTERS = {
  added: { work_order_id: null },
  payouts: { work_order_id: { $ne: null } },
};

const TAB_COLUMNS = {
  added: datatableAddedFundsColumns,
  payouts: datatablePayoutFundsColumns,
};

const EMPTY_TAB_FILTER = { searchText: '', startDate: null, endDate: null };
const EMPTY_TOTALS = { totalAmount: 0, totalFee: 0 };

const formatCurrency = (value) => Number(value || 0).toLocaleString('en-US', localCurrencySettings);

const ClientsManageFunds = ({ isReload }) => {
  const [activeTab, setActiveTab] = useState('added');
  const [tabFilters, setTabFilters] = useState({
    added: { ...EMPTY_TAB_FILTER },
    payouts: { ...EMPTY_TAB_FILTER },
  });
  const [totals, setTotals] = useState(EMPTY_TOTALS);

  const current = tabFilters[activeTab];
  const feeLabel = activeTab === 'added' ? 'Total Service Fee' : 'Total Tech Fee';

  const changeTab = (tabId) => {
    setTotals(EMPTY_TOTALS);
    setActiveTab(tabId);
  };

  const handleData = (data) => {
    setTotals({
      totalAmount: data?.totalAmount || 0,
      totalFee: data?.totalFee || 0,
    });
  };

  const updateCurrent = (patch) => {
    setTabFilters((prev) => ({
      ...prev,
      [activeTab]: { ...prev[activeTab], ...patch },
    }));
  };

  const buildFilters = (tabId) => {
    const { searchText, startDate, endDate } = tabFilters[tabId];
    const payload = { ...TAB_BASE_FILTERS[tabId] };
    const trimmed = searchText.trim();
    if (trimmed) {
      payload['company.name'] = { $regex: escapeRegExp(trimmed), $options: 'i' };
    }
    if (startDate || endDate) {
      payload.createdAt = {
        ...(startDate ? { $gte: toYmd(startDate) } : {}),
        ...(endDate ? { $lte: toYmd(endDate) } : {}),
      };
    }
    return payload;
  };

  const applyFilters = () => {
    emitApplyFilters(buildFilters(activeTab));
  };

  const resetFilters = () => {
    setTabFilters((prev) => ({
      ...prev,
      [activeTab]: { ...EMPTY_TAB_FILTER },
    }));
    emitApplyFilters(TAB_BASE_FILTERS[activeTab]);
  };

  const handleExport = () => {
    const filters = buildFilters(activeTab);
    axios
      .post('/api/admin/history/export', {
        params: { filters, tab: activeTab, sortBy: 'updatedAt', sortDirection: -1 },
        responseType: 'blob',
      })
      .then((res) => {
        const link = document.createElement('a');
        const url = window.URL.createObjectURL(new Blob([res.data.file], { type: 'text/csv' }));
        link.href = url;
        link.setAttribute('download', 'file.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((err) => console.error('Export failed', err));
  };

  return (
    <div className="subpage-wrap subpage-wrap--admin">
      <div className="subpage-header">
        <h1 className="subpage-title">Platform Fee Transactions History</h1>
      </div>

      <div className="subpage-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`subpage-tab${activeTab === tab.id ? ' subpage-tab--active' : ''}`}
            onClick={() => changeTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      
      <div className="subpage-summary">
        <div className="subpage-summary__item">
          <span className="subpage-summary__label">Total Funding Amount</span>
          <span className="subpage-summary__value">{formatCurrency(totals.totalAmount)}</span>
        </div>
        <div className="subpage-summary__item">
          <span className="subpage-summary__label">{feeLabel}</span>
          <span className="subpage-summary__value">{formatCurrency(totals.totalFee)}</span>
        </div>
      </div>

      <div className="subpage-filters" role="search">
        <input
          type="text"
          className="dashboard-input subpage-filters__search"
          name="search"
          maxLength={50}
          aria-label="Search by client or company name"
          placeholder="Search by client/company name"
          value={current.searchText}
          onChange={(e) => updateCurrent({ searchText: e.target.value })}
        />
        <DatePicker
          selected={current.startDate}
          onChange={(date) => updateCurrent({ startDate: date })}
          dateFormat="MM/dd/yyyy"
          placeholderText="From"
          isClearable
          className="dashboard-input subpage-filters__date"
        />
        <DatePicker
          selected={current.endDate}
          onChange={(date) => updateCurrent({ endDate: date })}
          dateFormat="MM/dd/yyyy"
          placeholderText="To"
          isClearable
          className="dashboard-input subpage-filters__date"
        />
        <div className="subpage-filters__actions">
          <button type="button" className="subpage-btn subpage-btn--ghost" onClick={resetFilters}>
            Reset
          </button>
          <button type="button" className="subpage-btn subpage-btn--gold" onClick={applyFilters}>
            Apply
          </button>
          <button type="button" className="subpage-btn subpage-btn--mint" onClick={handleExport}>
            <DashboardIcon name="export" size={18} strokeWidth="2.5" />
            Export
          </button>
        </div>
      </div>



      <div className="subpage-table-scroll">
        <ResourceDataTable
          key={activeTab}
          entity="/api/admin/payment/history"
          columns={TAB_COLUMNS[activeTab]}
          filters={buildFilters(activeTab)}
          isReload={isReload}
          onData={handleData}
          className="subpage-datatable"
        />
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  toggleModal,
};

const mapStateToProps = (state) => ({
  profile: state.profile,
  isReload: state.applicationState.isReload,
});

export default connect(mapStateToProps, mapDispatchToProps)(ClientsManageFunds);
